import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { preauthApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { MagnifyingGlass, Funnel, Eye, ShieldCheck } from '@phosphor-icons/react';

const formatStatus = (status) => {
  return status?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || '';
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(value || 0);
};

const PreAuthPage = () => {
  const navigate = useNavigate();
  const [preauths, setPreauths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadPreauths();
  }, [statusFilter]);

  const loadPreauths = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await preauthApi.list(params);
      setPreauths(res.data);
    } catch (error) {
      console.error('Failed to load pre-auths:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'submitted': case 'under_review': return 'bg-blue-100 text-blue-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <div className="space-y-6" data-testid="preauth-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light font-['Outfit'] text-[#1C1C1C]">Pre-Authorizations</h1>
          <p className="text-[#595959] mt-1">Manage pre-authorization requests</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 border-[#E5E5E5]">
                <Funnel className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-xs text-[#595959] uppercase">Total Requests</p>
            <p className="text-2xl font-semibold text-[#1C1C1C] font-['Outfit']">{preauths.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#FEF3CD] border-0">
          <CardContent className="p-4">
            <p className="text-xs text-[#664D03] uppercase">Pending</p>
            <p className="text-2xl font-semibold text-[#664D03] font-['Outfit']">
              {preauths.filter(p => ['draft', 'submitted', 'under_review'].includes(p.status)).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-[#D1E7DD] border-0">
          <CardContent className="p-4">
            <p className="text-xs text-[#0F5132] uppercase">Approved</p>
            <p className="text-2xl font-semibold text-[#0F5132] font-['Outfit']">
              {preauths.filter(p => p.status === 'approved').length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-[#F8D7DA] border-0">
          <CardContent className="p-4">
            <p className="text-xs text-[#842029] uppercase">Rejected</p>
            <p className="text-2xl font-semibold text-[#842029] font-['Outfit']">
              {preauths.filter(p => p.status === 'rejected').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D4A3E]"></div>
            </div>
          ) : preauths.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center p-6">
              <ShieldCheck className="h-16 w-16 text-[#A3A3A3] mb-4" weight="light" />
              <p className="text-lg font-medium text-[#1C1C1C]">No pre-authorizations found</p>
              <p className="text-sm text-[#595959] mt-1">Pre-auth requests will appear here</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F5F0] hover:bg-[#F5F5F0]">
                  <TableHead className="font-semibold text-[#1C1C1C]">PA Number</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Case No</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Claim No</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Patient</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Payer</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Requested</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Approved</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Status</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C]">Date</TableHead>
                  <TableHead className="font-semibold text-[#1C1C1C] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preauths.map((pa) => (
                  <TableRow key={pa.id} className="table-row-hover cursor-pointer" onClick={() => navigate(`/claims/${pa.claim_id}`)}>
                    <TableCell className="font-medium text-[#2D4A3E]">{pa.preauth_number}</TableCell>
                    <TableCell>{pa.claim_number}</TableCell>
                    <TableCell className="text-xs font-mono text-orange-600">{pa.insurance_claim_no || '-'}</TableCell>
                    <TableCell>{pa.patient_name}</TableCell>
                    <TableCell>{pa.payer_name}</TableCell>
                    <TableCell>{formatCurrency(pa.requested_amount)}</TableCell>
                    <TableCell className="text-green-600">{formatCurrency(pa.approved_amount)}</TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeClass(pa.status)}>{formatStatus(pa.status)}</Badge>
                    </TableCell>
                    <TableCell className="text-[#595959]">{new Date(pa.created_at).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/claims/${pa.claim_id}`); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PreAuthPage;
